/**
 * LotoWheel Modal — Tab Pozos Comunes: la colección de jackpots del sorteo.
 *
 * Dos presentaciones sobre el MISMO endpoint y el MISMO recolector:
 *   - modo vigente: la lista de pozos con checkbox que opera hoy. Admite pozos
 *     con la configuración multinivel pendiente y no los desconecta.
 *   - editor multinivel: hasta cuatro posiciones Grand → Major → Minor → Mini,
 *     cada una con un pozo activo de ese nivel o vacía. Solo se entra por
 *     conversión EXPLÍCITA del operador; el body viaja con `modoMultinivel:
 *     true` y el backend exige niveles completos y únicos.
 *
 * El body lo arma UNA sola autoridad, `_recolectarSuscripcionesPozosComunes`,
 * para el botón del tab y para el Guardar del modal (lotowheel.js). La lógica
 * de parseo y armado vive en `coleccion-de-pozos-recolector.js`, pura y
 * probada sin DOM.
 *
 * El operador autorizado SÍ ve el umbral de disparo: este panel consume
 * `/admin/pozos-comunes`. Lo que nunca lo expone son `/state` y `/configs`.
 */

import { LotowheelModule } from './lotowheel.js';
import { App, Api } from '../../../admin.js';
import { t } from '../../../services/i18n-admin.js';
import { escapeHtmlParaInyeccionSegura } from '../../../utils/escape-html-para-inyeccion-segura.js';
import {
    NIVELES_DE_POZO,
    construirSlotsDeColeccion,
    configuracionMultinivelEstaCompleta,
} from '@shared/contracts/niveles-de-pozo.js';
import { construirBodyDeSuscripciones } from './coleccion-de-pozos-recolector.js';
import { pedirPreviewDeFinanciacion, confirmarYFinanciarColeccion } from './financiacion-de-coleccion.js';

const ETIQUETA_DE_NIVEL = { grand: 'Grand', major: 'Major', minor: 'Minor', mini: 'Mini' };

Object.assign(LotowheelModule, {
    // ====================================================================
    // POZOS COMUNES — Panel de suscripciones en modal de sorteo
    // ====================================================================

    /**
     * Carga el panel de pozos comunes para un sorteo dado.
     */
    async loadPozoComunesPanel(sorteoId) {
        const list = document.getElementById('pozosComunesListSorteo');
        if (!list) return;

        // Siempre recargar desde API (los pozos disponibles pueden cambiar entre aperturas)
        try {
            const data = await Api.callAdmin('/admin/pozos-comunes', 'GET');
            this._pozosComunesDisponibles = Array.isArray(data) ? data : [];
        } catch (e) {
            this._pozosComunesDisponibles = [];
        }

        this._suscripcionesPozosComunes = [];
        if (sorteoId) {
            try {
                const subs = await Api.callAdmin(`/admin/sorteos/${sorteoId}/pozos-comunes`, 'GET');
                this._suscripcionesPozosComunes = Array.isArray(subs) ? subs : [];
            } catch (e) {
                // Sin suscripciones previas
            }
        }

        this._renderPozoComunesPanel();
    },

    /**
     * La colección puede convertirse al editor multinivel solo cuando ninguna
     * suscripción vigente es de un pozo con configuración pendiente (el backend
     * los rechaza en modo multinivel, y acá no se inventan niveles) y existe al
     * menos un pozo activo con nivel para ubicar.
     */
    _puedeConvertirseAMultinivel(coleccion) {
        const hayPendienteSuscrito = coleccion.pendientes.some(p => p.suscripcion !== null);
        const hayPozoConNivel = coleccion.slots.some(s => s.opciones.length > 0);
        return !hayPendienteSuscrito && hayPozoConNivel;
    },

    _renderPozoComunesPanel() {
        const list = document.getElementById('pozosComunesListSorteo');
        if (!list) return;

        if (this._pozosComunesDisponibles.length === 0) {
            list.innerHTML =
                '<div style="text-align:center; padding:32px 16px; color:var(--text-muted);">' +
                '<div style="font-size:15px; font-weight:500; margin-bottom:4px;">' +
                t('Sin jackpots disponibles') +
                '</div>' +
                '<div style="font-size:13px;">' +
                t('Crea un jackpot en la sección "Pozos Comunes" del menú lateral.') +
                '</div>' +
                '</div>';
            return;
        }

        const coleccion = construirSlotsDeColeccion(this._pozosComunesDisponibles, this._suscripcionesPozosComunes);
        // El modo se decide UNA vez por apertura (lotowheel.js lo pone en null al
        // abrir otro sorteo): la conversión es explícita, nunca implícita. Una
        // colección que ya está completa en niveles arranca en el editor; una
        // con pozos pendientes o vacía sin pozos con nivel, en el modo vigente.
        if (this._editorMultinivelActivo === null || this._editorMultinivelActivo === undefined) {
            this._editorMultinivelActivo = this._puedeConvertirseAMultinivel(coleccion) && coleccion.evaluacion.lista;
        }

        list.innerHTML =
            this._htmlCabeceraDeColeccion(coleccion) +
            (this._editorMultinivelActivo ? this._htmlEditorMultinivel(coleccion) : this._htmlModoVigente(coleccion));

        for (let i = 0; i < coleccion.slots.length; i++) {
            const nivel = coleccion.slots[i].nivel;
            const selector = document.getElementById(`pozo_slot_${nivel}`);
            if (selector) selector.onchange = () => this._onPozoSlotChange(nivel);
        }
        if (this._editorMultinivelActivo) this._refrescarPreviewDeFinanciacion();
    },

    alternarEditorMultinivel() {
        const coleccion = construirSlotsDeColeccion(this._pozosComunesDisponibles, this._suscripcionesPozosComunes);
        if (!this._editorMultinivelActivo && !this._puedeConvertirseAMultinivel(coleccion)) {
            App.toast(
                t(
                    'Para convertir la colección, completar la configuración multinivel de los pozos pendientes suscritos o quitarlos en el modo vigente.'
                ),
                'error'
            );
            return;
        }
        this._editorMultinivelActivo = !this._editorMultinivelActivo;
        this._renderPozoComunesPanel();
    },

    _htmlCabeceraDeColeccion(coleccion) {
        const evaluacion = coleccion.evaluacion;
        const MOTIVO = {
            sin_pozos: t('Sin pozos en la colección'),
            configuracion_pendiente: t(
                'Hay pozos con configuración multinivel pendiente: el motor vigente sigue igual'
            ),
            nivel_duplicado: t('Dos pozos comparten nivel'),
        };
        const estado = evaluacion.lista
            ? `<span class="config-badge" style="color:var(--accent-green);border-color:var(--accent-green);">${t('Colección lista para el motor multinivel')}</span>`
            : `<span class="config-badge" style="opacity:0.8;">${MOTIVO[evaluacion.motivo] || evaluacion.motivo}</span>`;
        const puedeConvertir = this._puedeConvertirseAMultinivel(coleccion);
        const textoBoton = this._editorMultinivelActivo
            ? t('Volver al modo vigente')
            : t('Convertir a colección multinivel');
        const ayuda = this._editorMultinivelActivo
            ? t(
                  'Una colección con hasta cuatro pozos independientes: cada posición es un pozo activo de ese nivel, o queda vacía.'
              )
            : puedeConvertir
              ? t('El modo vigente sigue operando igual. Convertir a colección multinivel es una acción explícita.')
              : t(
                    'Para convertir la colección, completar la configuración multinivel de los pozos pendientes suscritos o quitarlos en el modo vigente.'
                );
        return `
            <div id="pozosColeccionCabecera" style="display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; margin-bottom:12px;">
              <div style="display:flex; gap:6px; flex-wrap:wrap; align-items:center;">
                <span class="config-badge">${this._editorMultinivelActivo ? t('Editor multinivel') : t('Modo vigente')}</span>
                ${estado}
              </div>
              <button type="button" class="btn btn-sm btn-secondary" id="pozosAlternarEditorBtn"
                data-action="lotowheel:alternarEditorMultinivel" ${!this._editorMultinivelActivo && !puedeConvertir ? 'disabled' : ''}>
                ${textoBoton}
              </button>
            </div>
            <p style="font-size:13px; color:var(--text-muted); line-height:1.5; margin:0 0 12px;">${ayuda}</p>`;
    },

    /** Bote actual y progreso hacia el umbral: información del operador autorizado. */
    _htmlBoteYUmbral(p) {
        const progresoPct = p.pozo_umbral > 0 ? Math.min(100, Math.round((p.pozo_actual / p.pozo_umbral) * 100)) : 0;
        return `
            <div style="margin:8px 0 4px;">
              <div style="background:var(--border-color); border-radius:4px; height:6px; overflow:hidden;">
                <div style="height:100%; width:${progresoPct}%; background:var(--color-primary, #4f6ef7); border-radius:4px; transition:width 0.3s;"></div>
              </div>
              <div style="font-size:12px; color:var(--text-muted); margin-top:4px;">
                ${progresoPct}${t('% del umbral de disparo (')}${App.formatMoney(p.pozo_umbral, p.moneda)})
              </div>
            </div>`;
    },

    _htmlBadgeDeNivel(p) {
        return configuracionMultinivelEstaCompleta(p)
            ? `<span class="config-badge">${ETIQUETA_DE_NIVEL[p.nivel]}</span>`
            : `<span class="config-badge" style="color:var(--accent-orange);border-color:var(--accent-orange);">${t('Configuración multinivel pendiente')}</span>`;
    },

    /**
     * El reparto del aporte pertenece al POZO, no a la colección: en el editor
     * multinivel se muestra, no se edita. Un override por sorteo cambiaría el
     * total sin decir cuánto va a lo visible y cuánto a la reserva.
     */
    _htmlRepartoDelPozo(pozo) {
        if (!pozo) return '';
        return `
            <div style="display:flex; gap:18px; flex-wrap:wrap; font-size:12px; color:var(--text-muted); margin-top:10px; padding-top:10px; border-top:1px solid var(--border-color);">
              <div>${t('Aporte total del pozo')}: <strong style="color:var(--text-primary);">${pozo.porcentaje_aporte_defecto}%</strong></div>
              <div>${t('Visible')}: <strong style="color:var(--text-primary);">${pozo.porcentaje_aporte_visible}%</strong></div>
              <div>${t('Reserva')}: <strong style="color:var(--text-primary);">${pozo.porcentaje_aporte_reserva}%</strong></div>
            </div>
            <div style="font-size:11px; color:var(--text-muted); margin-top:4px;">${t('Se configura en el pozo, no por sorteo.')}</div>`;
    },

    /** Solo el control de pausa: en multinivel no hay override de aporte. */
    _htmlPausaDeSuscripcion({ pausaId, pausa, habilitado }) {
        const opcionesPausa = [
            [0, t('Sin pausa')],
            [15, t('15 segundos')],
            [30, t('30 segundos')],
            [60, t('1 minuto')],
        ]
            .map(
                ([valor, etiqueta]) =>
                    `<option value="${valor}" ${pausa === valor ? 'selected' : ''}>${etiqueta}</option>`
            )
            .join('');
        return `
            <div style="margin-top:10px; ${habilitado ? '' : 'opacity:0.4; pointer-events:none;'}">
              <label style="display:block; font-size:12px; font-weight:500; color:var(--text-muted); margin-bottom:4px;">
                ${t('Pausa al disparar')}
              </label>
              <select id="${pausaId}" class="form-select" style="width:140px; font-size:14px;" ${habilitado ? '' : 'disabled'}>
                ${opcionesPausa}
              </select>
            </div>`;
    },

    _htmlControlesDeSuscripcion({ pctId, pausaId, porcentaje, pausa, defecto, habilitado }) {
        const opcionesPausa = [
            [0, t('Sin pausa')],
            [15, t('15 segundos')],
            [30, t('30 segundos')],
            [60, t('1 minuto')],
        ]
            .map(
                ([valor, etiqueta]) =>
                    `<option value="${valor}" ${pausa === valor ? 'selected' : ''}>${etiqueta}</option>`
            )
            .join('');
        return `
            <div style="display:flex; gap:16px; flex-wrap:wrap; margin-top:12px; padding-top:10px; border-top:1px solid var(--border-color); ${habilitado ? '' : 'opacity:0.4; pointer-events:none;'}">
              <div>
                <label style="display:block; font-size:12px; font-weight:500; color:var(--text-muted); margin-bottom:4px;">
                  ${t('% de cada apuesta que aporta')}
                </label>
                <input type="number" id="${pctId}" value="${porcentaje ?? ''}"
                  placeholder="${defecto} (${t('hereda el defecto')})"
                  min="0.01" max="100" step="0.1"
                  class="form-input"
                  style="width:160px; font-size:14px; text-align:center;"
                  title="${t('Vacío = hereda el % por defecto del pozo, aunque cambie después. Un número = override fijo para este sorteo.')}"
                  ${habilitado ? '' : 'disabled'}>
              </div>
              <div>
                <label style="display:block; font-size:12px; font-weight:500; color:var(--text-muted); margin-bottom:4px;">
                  ${t('Pausa al disparar')}
                </label>
                <select id="${pausaId}" class="form-select" style="width:140px; font-size:14px;" ${habilitado ? '' : 'disabled'}>
                  ${opcionesPausa}
                </select>
              </div>
            </div>`;
    },

    /** La lista que opera hoy: cada pozo con su checkbox. Sin cambios de contrato. */
    _htmlModoVigente() {
        let html = '';
        for (let i = 0; i < this._pozosComunesDisponibles.length; i++) {
            const p = this._pozosComunesDisponibles[i];
            const sus = this._suscripcionesPozosComunes.find(s => s.pozo_id === p.id);
            const isSuscrito = sus !== undefined;
            // porcentaje_aporte null = hereda el defecto configurado en el pozo
            const pct = isSuscrito && sus.porcentaje_aporte !== null ? sus.porcentaje_aporte : '';
            const paralizar = isSuscrito ? sus.paralizar_segundos || 0 : 0;

            html += `
                <div id="pozoRow_${p.id}" style="
                    background:var(--bg-card, var(--bg-secondary));
                    border:1px solid ${isSuscrito ? 'var(--color-primary, #4f6ef7)' : 'var(--border-color)'};
                    border-radius:10px;
                    padding:16px;
                    margin-bottom:10px;
                    transition: border-color 0.15s;
                ">
                  <div style="display:flex; align-items:flex-start; gap:14px;">
                    <input type="checkbox" id="pozo_check_${p.id}" ${isSuscrito ? 'checked' : ''}
                      data-action="lotowheel:onPozoComunToggle" data-id="${p.id}"
                      style="width:18px; height:18px; cursor:pointer; flex-shrink:0; margin-top:2px; accent-color:var(--color-primary, #4f6ef7);">
                    <div style="flex:1; min-width:0;">
                      <div style="display:flex; align-items:baseline; justify-content:space-between; gap:8px; flex-wrap:wrap;">
                        <div style="font-size:15px; font-weight:600; color:var(--text-primary); display:flex; gap:8px; align-items:center; flex-wrap:wrap;">
                          ${escapeHtmlParaInyeccionSegura(p.nombre)} ${this._htmlBadgeDeNivel(p)}
                        </div>
                        <div style="font-size:14px; font-weight:700; color:var(--color-primary, #4f6ef7); white-space:nowrap;">
                          ${t('Bote: ')}${App.formatMoney(p.pozo_actual, p.moneda)}
                        </div>
                      </div>
                      ${this._htmlBoteYUmbral(p)}
                      ${this._htmlControlesDeSuscripcion({
                          pctId: `pozo_pct_${p.id}`,
                          pausaId: `pozo_paralizar_${p.id}`,
                          porcentaje: pct,
                          pausa: paralizar,
                          defecto: p.porcentaje_aporte_defecto,
                          habilitado: isSuscrito,
                      })}
                    </div>
                  </div>
                </div>`;
        }
        return html;
    },

    /** Hasta cuatro posiciones, Grand → Major → Minor → Mini, cada una con un pozo de su nivel o vacía. */
    _htmlEditorMultinivel(coleccion) {
        let html = '';
        for (let i = 0; i < coleccion.slots.length; i++) {
            const slot = coleccion.slots[i];
            const seleccionado = slot.seleccionado;
            const sus = slot.suscripcion;
            // La opción conserva su texto (nombre + bote formateado); lo único
            // nuevo es el `<optgroup>` que las reparte por grupo. Los pozos sin
            // grupo van sueltos: envolverlos daría una categoría inexistente.
            const htmlDeOpcion = p =>
                `<option value="${p.id}" ${seleccionado && seleccionado.id === p.id ? 'selected' : ''}>${escapeHtmlParaInyeccionSegura(p.nombre)} · ${App.formatMoney(p.pozo_actual, p.moneda)}</option>`;
            const opciones = slot.opcionesPorGrupo
                .map(({ grupo, opciones: delGrupo }) => {
                    const items = delGrupo.map(htmlDeOpcion).join('');
                    if (grupo === null) return items;
                    return `<optgroup label="${escapeHtmlParaInyeccionSegura(grupo)}">${items}</optgroup>`;
                })
                .join('');
            const sinOpciones = slot.opciones.length === 0;
            html += `
                <div id="pozoSlot_${slot.nivel}" style="
                    background:var(--bg-card, var(--bg-secondary));
                    border:1px solid ${seleccionado ? 'var(--color-primary, #4f6ef7)' : 'var(--border-color)'};
                    border-radius:10px; padding:16px; margin-bottom:10px;">
                  <div style="display:flex; align-items:center; justify-content:space-between; gap:8px; flex-wrap:wrap;">
                    <div style="font-size:15px; font-weight:700; color:var(--text-primary);">
                      ${ETIQUETA_DE_NIVEL[slot.nivel]}
                      ${seleccionado ? '' : `<span class="config-badge" style="margin-left:8px; opacity:0.7;">${t('Posición vacía')}</span>`}
                    </div>
                    ${
                        seleccionado
                            ? `<div style="font-size:14px; font-weight:700; color:var(--color-primary, #4f6ef7); white-space:nowrap;">${t('Bote: ')}${App.formatMoney(seleccionado.pozo_actual, seleccionado.moneda)}</div>`
                            : ''
                    }
                  </div>
                  <select id="pozo_slot_${slot.nivel}" class="form-select" style="margin-top:10px; width:100%;" ${sinOpciones ? 'disabled' : ''}>
                    <option value="">${t('Sin pozo en esta posición')}</option>
                    ${opciones}
                  </select>
                  ${
                      sinOpciones
                          ? `<div style="font-size:12px; color:var(--text-muted); margin-top:6px;">${t('No hay pozos activos de este nivel. Crear uno en Pozos Comunes o completar la configuración de uno pendiente.')}</div>`
                          : ''
                  }
                  <div id="pozoSlotDetalle_${slot.nivel}">${
                      seleccionado ? this._htmlBoteYUmbral(seleccionado) + this._htmlRepartoDelPozo(seleccionado) : ''
                  }</div>
                  ${this._htmlPausaDeSuscripcion({
                      pausaId: `pozo_slot_paralizar_${slot.nivel}`,
                      pausa: sus ? sus.paralizar_segundos || 0 : 0,
                      habilitado: Boolean(seleccionado),
                  })}
                </div>`;
        }
        if (coleccion.pendientes.length > 0) {
            html += `<p style="font-size:12px; color:var(--text-muted); margin:4px 0 0;">${coleccion.pendientes.length} ${t('pozo(s) con configuración multinivel pendiente no se ofrecen en las posiciones. Completar su configuración en Pozos Comunes.')}</p>`;
        }
        html += this._htmlFinanciarColeccion(coleccion);
        return html;
    },

    /**
     * SEED-1 — "Financiar y activar colección".
     *
     * El operador ve los cuatro importes y el total que devuelve el BACKEND
     * (`/coleccion/preview`); el front no calcula nada. Un botón, una
     * confirmación, sin lenguaje técnico.
     */
    _htmlFinanciarColeccion(coleccion) {
        const elegidos = coleccion.slots.filter(s => s.seleccionado);
        if (elegidos.length !== NIVELES_DE_POZO.length) {
            return `
                <div style="margin-top:14px; padding:14px; border:1px dashed var(--border-color); border-radius:10px;">
                  <div style="font-size:13px; color:var(--text-muted);">
                    ${t('Elige un acumulado en cada una de las cuatro posiciones para poder activarlos.')}
                  </div>
                </div>`;
        }
        const preview = this._previewDeFinanciacion;
        const yaFinanciada = preview && preview.yaFinanciados && preview.yaFinanciados.length > 0;
        const filas = preview
            ? preview.semillas
                  .map(
                      s => `
                    <div style="display:flex; justify-content:space-between; gap:12px; font-size:14px; padding:3px 0;">
                      <span style="color:var(--text-muted);">${ETIQUETA_DE_NIVEL[s.nivel]}</span>
                      <strong style="color:var(--text-primary);">${App.formatMoney(s.baseCentimos, preview.moneda)}</strong>
                    </div>`
                  )
                  .join('') +
              `<div style="display:flex; justify-content:space-between; gap:12px; font-size:15px; padding:8px 0 0; margin-top:6px; border-top:1px solid var(--border-color);">
                 <strong style="color:var(--text-primary);">${t('Total')}</strong>
                 <strong style="color:var(--color-primary, #4f6ef7);">${App.formatMoney(preview.totalCentimos, preview.moneda)}</strong>
               </div>`
            : `<div style="font-size:13px; color:var(--text-muted);">${t('Calculando los importes…')}</div>`;

        return `
            <div id="pozosFinanciarColeccion" style="margin-top:14px; padding:16px; background:var(--bg-card, var(--bg-secondary)); border:1px solid var(--border-color); border-radius:10px;">
              <div style="font-size:15px; font-weight:700; color:var(--text-primary); margin-bottom:10px;">
                ${t('Financiar y activar colección')}
              </div>
              ${filas}
              <p style="font-size:13px; color:var(--text-muted); line-height:1.5; margin:12px 0;">
                ${t('Se registrará la financiación inicial de los cuatro acumulados. Los cuatro quedarán visibles y disponibles al activar.')}
              </p>
              <button type="button" class="btn btn-primary" id="pozosFinanciarBtn"
                data-action="lotowheel:financiarYActivarColeccion" ${yaFinanciada || !preview ? 'disabled' : ''}>
                ${yaFinanciada ? t('Ya financiada') : t('Financiar y activar')}
              </button>
            </div>`;
    },

    /** Pide al backend el preview de la colección elegida y repinta. */
    async _refrescarPreviewDeFinanciacion() {
        const elegidos = NIVELES_DE_POZO.map(nivel => document.getElementById(`pozo_slot_${nivel}`)?.value).filter(
            Boolean
        );
        if (elegidos.length !== NIVELES_DE_POZO.length) {
            this._previewDeFinanciacion = null;
            return;
        }
        try {
            this._previewDeFinanciacion = await pedirPreviewDeFinanciacion(elegidos);
        } catch (e) {
            this._previewDeFinanciacion = null;
        }
        const contenedor = document.getElementById('pozosFinanciarColeccion');
        if (contenedor) {
            const coleccion = construirSlotsDeColeccion(this._pozosComunesDisponibles, this._suscripcionesPozosComunes);
            contenedor.outerHTML = this._htmlFinanciarColeccion(coleccion);
        }
    },

    /**
     * Una sola acción: financiar las cuatro semillas y activar la colección.
     *
     * `_financiacionEnProgreso` cubre el doble tap: la operación es de dinero y
     * el backend la deduplica por key, pero un segundo click no debe siquiera
     * salir. La key se genera UNA vez por intento y se reusa en el reintento.
     */
    async financiarYActivarColeccion() {
        if (this._financiacionEnProgreso) return;
        const preview = this._previewDeFinanciacion;
        const sorteoId = document.getElementById('lotowheelConfigId')?.value;
        if (!preview || !sorteoId) return;

        this._financiacionEnProgreso = true;
        const boton = document.getElementById('pozosFinanciarBtn');
        if (boton) boton.disabled = true;
        try {
            const resultado = await confirmarYFinanciarColeccion({ gameTypeCode: 'lotowheel', sorteoId, preview });
            if (resultado && resultado.ok) await this.loadPozoComunesPanel(sorteoId);
        } finally {
            this._financiacionEnProgreso = false;
            const botonActual = document.getElementById('pozosFinanciarBtn');
            if (botonActual) botonActual.disabled = false;
        }
    },

    _onPozoSlotChange(nivel) {
        const selector = document.getElementById(`pozo_slot_${nivel}`);
        if (!selector) return;
        const pozo = this._pozosComunesDisponibles.find(p => String(p.id) === selector.value) || null;
        const sus = pozo ? this._suscripcionesPozosComunes.find(s => s.pozo_id === pozo.id) : null;
        const pausaInput = document.getElementById(`pozo_slot_paralizar_${nivel}`);
        if (pausaInput) {
            pausaInput.disabled = !pozo;
            pausaInput.value = String(sus ? sus.paralizar_segundos || 0 : 0);
            const contenedorPausa = pausaInput.parentElement;
            if (contenedorPausa) {
                contenedorPausa.style.opacity = pozo ? '1' : '0.4';
                contenedorPausa.style.pointerEvents = pozo ? '' : 'none';
            }
        }
        // Bote, umbral y reparto del pozo elegido: todo de lectura.
        const detalle = document.getElementById(`pozoSlotDetalle_${nivel}`);
        if (detalle) detalle.innerHTML = pozo ? this._htmlBoteYUmbral(pozo) + this._htmlRepartoDelPozo(pozo) : '';
        const card = document.getElementById(`pozoSlot_${nivel}`);
        if (card) card.style.borderColor = pozo ? 'var(--color-primary, #4f6ef7)' : 'var(--border-color)';
        // El total lo calcula el backend sobre los pozos elegidos AHORA: cambiar
        // una posición invalida el importe que el operador tenía a la vista.
        this._previewDeFinanciacion = null;
        this._refrescarPreviewDeFinanciacion();
    },

    _onPozoComunToggle(pozoId, isChecked) {
        const pctInput = document.getElementById(`pozo_pct_${pozoId}`);
        const paralizarInput = document.getElementById(`pozo_paralizar_${pozoId}`);
        if (pctInput) pctInput.disabled = !isChecked;
        if (paralizarInput) paralizarInput.disabled = !isChecked;
        // Quitar/poner pointer-events:none en el container de controles
        const row = document.getElementById(`pozoRow_${pozoId}`);
        if (row) {
            const controls = row.querySelector('[style*="pointer-events"]');
            if (controls) {
                controls.style.opacity = isChecked ? '1' : '0.4';
                controls.style.pointerEvents = isChecked ? '' : 'none';
            }
            // Actualizar borde del card para feedback visual
            row.style.borderColor = isChecked ? 'var(--color-primary, #4f6ef7)' : 'var(--border-color)';
        }
    },

    /**
     * ÚNICA autoridad que lee los controles y arma `{ suscripciones,
     * modoMultinivel }`. La usan el botón del tab y el Guardar del modal.
     */
    _recolectarSuscripcionesPozosComunes() {
        const valorDe = id => document.getElementById(id)?.value ?? '';
        // Sin `porcentaje`: en multinivel el aporte sale del pozo y la suscripción
        // viaja con NULL. No hay control que leer porque no debe existir.
        const slots = NIVELES_DE_POZO.map(nivel => ({
            nivel,
            pozoId: valorDe(`pozo_slot_${nivel}`) || null,
            pausa: valorDe(`pozo_slot_paralizar_${nivel}`),
        }));
        const filas = this._pozosComunesDisponibles.map(p => ({
            pozoId: p.id,
            marcada: document.getElementById(`pozo_check_${p.id}`)?.checked === true,
            porcentaje: valorDe(`pozo_pct_${p.id}`),
            pausa: valorDe(`pozo_paralizar_${p.id}`),
        }));
        return construirBodyDeSuscripciones({
            editorMultinivelActivo: this._editorMultinivelActivo === true,
            slots,
            filas,
        });
    },

    async saveSuscripcionesPozosComunes() {
        const id = document.getElementById('lotowheelConfigId').value;
        if (!id) return;

        const body = this._recolectarSuscripcionesPozosComunes();
        const result = await Api.callAdmin(`/admin/sorteos/${id}/pozos-comunes`, 'PUT', body);
        if (result !== null) {
            App.toast(t('Suscripciones guardadas: ') + body.suscripciones.length + t(' pozo(s) activo(s)'));
            this._suscripcionesPozosComunes = body.suscripciones.map(s => ({
                pozo_id: s.pozo_id,
                porcentaje_aporte: s.porcentaje_aporte,
                paralizar_segundos: s.paralizar_segundos,
            }));
            this._renderPozoComunesPanel();
        }
    },
});
